import React, { useState, useEffect } from 'react'
import vehicleDailyTripService from '../../../services/vehicleDailyTripService'
import { X } from 'lucide-react'import { useTranslation } from 'react-i18next';


const ViewVehicleDailyTripModal = ({ tripId, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true)
  const [trip, setTrip] = useState(null)

  useEffect(() => {
    fetchTrip()
  }, [tripId])

  const fetchTrip = async () => {
    try {
      setLoading(true)
      const response = await vehicleDailyTripService.getOne(tripId)
      setTrip(response.data.data)
    } catch (err) {
      console.error('Failed to load trip:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-2xl">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!trip) {
    return null
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-xl font-bold text-secondary">Trip Details</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6">
            {/* Trip Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Trip Date</label>
              <p className="text-gray-900">{formatDate(trip.trip_date)}</p>
            </div>

            {/* Running KM */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Running KM</label>
              <p className="text-gray-900 font-semibold">{trip.running_km.toFixed(2)} KM</p>
            </div>

            {/* Opening KM */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Opening KM</label>
              <p className="text-gray-900">{trip.opening_km.toFixed(2)}</p>
            </div>

            {/* Closing KM */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Closing KM</label>
              <p className="text-gray-900">{trip.closing_km.toFixed(2)}</p>
            </div>

            {/* Daily Diff */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Daily Diff (KM)</label>
              <p className={`font-semibold ${trip.daily_diff > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {trip.daily_diff.toFixed(2)}
              </p>
            </div>

            {/* Extra KM Charge */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Extra KM Charge</label>
              <p className="text-gray-900 font-semibold">{formatCurrency(trip.extra_km_charge)}</p>
            </div>

            {/* Delivery Point */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Delivery Point</label>
              <p className="text-gray-900">{trip.delivery_point || 'N/A'}</p>
            </div>

            {/* Driver */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Driver</label>
              <p className="text-gray-900">{trip.driver || 'N/A'}</p>
            </div>

            {/* In Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">In Time</label>
              <p className="text-gray-900">{trip.in_time || 'N/A'}</p>
            </div>

            {/* Out Time */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Out Time</label>
              <p className="text-gray-900">{trip.out_time || 'N/A'}</p>
            </div>

            {/* Toll */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Toll</label>
              <p className="text-gray-900">{formatCurrency(trip.toll)}</p>
            </div>

            {/* Created By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.createdBy')}</label>
              <p className="text-gray-900">{trip.created_by}</p>
            </div>

            {/* Created At */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Created At</label>
              <p className="text-gray-900 text-sm">{new Date(trip.created_at).toLocaleString('en-IN')}</p>
            </div>

            {/* Updated By */}
            {trip.updated_by && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Updated By</label>
                <p className="text-gray-900">{trip.updated_by}</p>
              </div>
            )}

            {/* Updated At */}
            {trip.updated_at && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Updated At</label>
                <p className="text-gray-900 text-sm">{new Date(trip.updated_at).toLocaleString('en-IN')}</p>
              </div>
            )}
          </div>

          {/* Modal Actions */}
          <div className="mt-8">
            <button
              onClick={onClose}
              className="w-full px-6 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewVehicleDailyTripModal
